/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';

import closeSvg from 'src/assets/close.svg';

import { BenefitSettingsType, IBenefit, CommuterSettings } from 'src/interfaces';
import ReactModal from 'react-modal';
import { useTranslation } from 'react-i18next';
import { CloseIcon, SettingsContent, SettingsTitle, FormTitle } from './styles';
import { EmptySettingsForm } from '../empty-settings-form';
import { BeneficiariesSettingsForm } from '../beneficiaries-settings-form';
import { CommuterSettingsForm } from '../commuter-settings-form';
import { DependentsSettingsForm } from '../dependents-settings-form';
import { ElectedCoverageSettingsForm } from '../elected-coverage-settings-form';

const SettingsForm: React.FC<{
  benefit: IBenefit;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (benefit: IBenefit) => void;
  // eslint-disable-next-line react/require-default-props
  onRequestBack?: () => void;
}> = ({ benefit, onSubmit, onRequestBack }) => {
  if (benefit.settings?.type === BenefitSettingsType.ELECT_COVERAGE) {
    return <ElectedCoverageSettingsForm benefit={benefit} onSubmit={onSubmit} onRequestBack={onRequestBack} />;
  }
  if (!benefit.necessaryToProvideBeneficiariesInformation && benefit.employeeChoosesPayment === 'No') {
    // Add directly to cart
    if (benefit.dependents === 'EE' && !benefit.necessaryToProvideBeneficiariesInformation) {
      return <EmptySettingsForm benefit={benefit} onSubmit={onSubmit} />;
    }
    // Abre dependentes
    // return <DependentsSettingsForm benefit={benefit} onSubmit={onSubmit} />;
  }

  if (benefit.necessaryToProvideBeneficiariesInformation && benefit.employeeChoosesPayment === 'No') {
    // Add directly to cart
    if (benefit.dependents === 'EE' && !benefit.necessaryToProvideBeneficiariesInformation) {
      return <EmptySettingsForm benefit={benefit} onSubmit={onSubmit} />;
    }
    // Show dependents and %
    return <BeneficiariesSettingsForm benefit={benefit} onSubmit={onSubmit} onRequestBack={onRequestBack} />;
  }

  if (benefit.employeeChoosesPayment === 'Yes') {
    // Show commuter values
    return <CommuterSettingsForm benefit={benefit} onSubmit={onSubmit} onRequestBack={onRequestBack} />;
  }

  return <EmptySettingsForm benefit={benefit} onSubmit={onSubmit} />;
};

const SettingsComponentChain: React.FC<{
  benefit: IBenefit;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (benefit: IBenefit) => void;
  handleModal: () => void;
}> = ({ benefit, onSubmit, handleModal }) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [modifiedBenefit, setModifiedBenefit] = useState<IBenefit>(benefit);

  const dependentsNeeded = benefit.dependents !== 'EE';

  useEffect(() => {
    if (dependentsNeeded) {
      setCurrentStep(1);
    } else {
      setCurrentStep(2);
    }
  }, []);

  const handleNext = (b: IBenefit) => {
    setCurrentStep(prevStep => prevStep + 1);
    setModifiedBenefit(b);

    if (currentStep === 2) {
      onSubmit(b);
    }
  };

  const handleBack = () => {
    setCurrentStep(prevStep => prevStep - 1);

    if (currentStep === 1) handleModal();
    if (currentStep === 2 && !dependentsNeeded) handleModal();
  };

  // Render components based on the current step
  let componentToRender;
  switch (currentStep) {
    case 1:
      componentToRender = (
        <DependentsSettingsForm benefit={modifiedBenefit} onSubmit={handleNext} onRequestBack={handleBack} />
      );
      break;
    case 2:
      componentToRender = <SettingsForm benefit={modifiedBenefit} onSubmit={handleNext} onRequestBack={handleBack} />;
      break;
    // Add more cases for additional components if needed
    default:
      componentToRender = null;
  }

  return <div>{componentToRender}</div>;
};

export const SettingsModal: React.FC<{
  isOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  setIsOpen: (b: boolean) => void;
  benefit: IBenefit;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (benefit: IBenefit) => void;
}> = ({ benefit, onSubmit, isOpen, setIsOpen }) => {
  const { t } = useTranslation();

  const handleModal = () => {
    setIsOpen(!isOpen);
  };

  const modaltheme = (): React.CSSProperties => {
    switch (benefit.settings?.type) {
      case BenefitSettingsType.COMMUTER:
        return {
          width: '999px',
          height: '660px',
        };
      case BenefitSettingsType.MEDICAL:
        return {
          width: '940px',
          height: '440px',
        };

      default:
        return {
          width: '999px',
          height: '660px',
        };
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={handleModal}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '20px',
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f3f4f8',
          ...modaltheme(),
        },
      }}
    >
      <SettingsTitle>
        <div>
          <CloseIcon src={closeSvg} onClick={handleModal} />
        </div>
      </SettingsTitle>
      <SettingsContent>
        {benefit && <SettingsComponentChain benefit={benefit} onSubmit={onSubmit} handleModal={handleModal} />}
      </SettingsContent>
    </ReactModal>
  );
};
